import { StatusBanner, List, Text } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "api-types";
import dayjs from "dayjs";
import { FormattedMessage, useIntl } from "react-intl";
import { formatRevenueAmount, getEndDateForPeriodText, isInsufficientL60dRevenueAIR } from "shared-ui";

import { MESSAGES } from "../message-definitions";

export const InfoBanner = ({ airData }: { airData: FundingApplicationSchemas.AdditionalRevenueSourceAIRSchema }) => {
  const intl = useIntl();
  const message = (() => {
    if (
      airData.reasons.includes("insufficient_revenue_data") &&
      airData.reasons.includes("customer_revenue_not_sufficient_for_funding")
    ) {
      return (
        <>
          <Text>
            {intl.formatMessage({
              id: "funding-application: air revenue source mixed reasons banner intro",
              defaultMessage: "Please provide additional revenue data:",
            })}
          </Text>
          <List>
            <List.Item>
              <FormattedMessage
                id="funding-application: air revenue source mixed reasons missing_periods"
                defaultMessage="<p>For <s>{startPeriod} to {endPeriod}</s></p>"
                values={{
                  p: (chunks: any) => <Text>{chunks}</Text>,
                  s: (chunks: any) => <strong>{chunks}</strong>,
                  startPeriod: dayjs(airData.missing_period?.start).startOf("month").format("LL"),
                  endPeriod: getEndDateForPeriodText(airData.missing_period?.end).format("LL"),
                }}
              />
            </List.Item>
            <List.Item>
              <FormattedMessage
                id="funding-application: air revenue source mixed reasons threshold amount"
                defaultMessage="<p>Demonstrating an <s>average monthly revenue of over {thresholdAmount} over the last {insufficientMonths} months</s></p>"
                values={{
                  p: (chunks: any) => <Text>{chunks}</Text>,
                  s: (chunks: any) => <strong>{chunks}</strong>,
                  insufficientMonths: isInsufficientL60dRevenueAIR(airData) ? 2 : 6,
                  thresholdAmount: formatRevenueAmount(airData.mean_30d_revenue_threshold, intl),
                }}
              />
            </List.Item>
          </List>
        </>
      );
    }
    if (airData.reasons.includes("insufficient_revenue_data")) {
      return (
        <FormattedMessage
          id="funding-application: air revenue source insufficient_revenue_data banner"
          defaultMessage="<p>Please provide additional revenue data for {period} by connecting another eCommerce platform, or let us know why you are unable to do so.</p>"
          values={{
            p: (chunks: any) => <Text>{chunks}</Text>,
            period: (
              <strong key={`${airData.missing_period?.start}-${airData.missing_period?.end}`}>
                {intl.formatMessage(MESSAGES.requiredPeriodText, {
                  dateFrom: dayjs(airData.missing_period?.start).startOf("month").format("LL"),
                  dateTo: getEndDateForPeriodText(airData.missing_period?.end).format("LL"),
                })}
              </strong>
            ),
          }}
        />
      );
    }
    if (airData.reasons.includes("customer_revenue_not_sufficient_for_funding")) {
      return (
        <FormattedMessage
          id="funding-application: air revenue source revenue threshold banner"
          defaultMessage="<p>Please provide additional revenue data demonstrating an <s>average monthly revenue of over {thresholdAmount} over the last {insufficientMonths} months</s> by connecting another eCommerce platform, or let us know why you are unable to do so.</p>"
          values={{
            p: (chunks: any) => <Text>{chunks}</Text>,
            s: (chunks: any) => <strong>{chunks}</strong>,
            insufficientMonths: isInsufficientL60dRevenueAIR(airData) ? 2 : 6,
            thresholdAmount: formatRevenueAmount(airData.mean_30d_revenue_threshold, intl),
          }}
        />
      );
    }
  })();

  return <StatusBanner variant="info" subheading={message} />;
};
